import { useState, useEffect, useCallback } from 'react';
import { User } from 'firebase/auth';
import { 
  Container, 
  Box, 
  Paper, 
  CardContent,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  AppBar,
  Toolbar,
  Typography
} from '@mui/material';
import { auth } from './firebase/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import LoginIcon from '@mui/icons-material/Login';
import PeopleIcon from '@mui/icons-material/People';
import ScoreboardIcon from '@mui/icons-material/Scoreboard';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import MenuIcon from '@mui/icons-material/Menu';
import FirebaseScoreService from './services/FirebaseScoreService';
import Authentication from './components/Authentication';
import Leaderboard from './components/Leaderboard';
import MembersComponent from './components/Members';
import Scores from './components/Scores';

interface Member {
  name: string;
  score?: number;
  icon?: string;
}

interface Members {
  [key: string]: Member;
}

function App() {
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<number>(0); // Main menu tab - default to Scores (index 0)
  const [currentMemberId, setCurrentMemberId] = useState<string | null>(null);
  const [members, setMembers] = useState<Members>({}); // Store all members for tabs
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null); // Menu anchor element
  const menuOpen = Boolean(menuAnchorEl);
  
  // Load all members - memoized to prevent infinite re-renders
  const loadAllMembers = useCallback(async () => {
    try {
      const membersData = await FirebaseScoreService.getMembers();
      const validMembers = Object.fromEntries(
        Object.entries(membersData).filter(([_, memberInfo]) => memberInfo !== null)
      ) as Members;
      setMembers(validMembers);
      return validMembers;
    } catch (error) {
      console.error('Error loading members:', error);
      return {};
    }
  }, []);
  
  // Load all members when component mounts if Scores tab is active
  useEffect(() => {
    if (activeTab === 2) {
      loadAllMembers();
    }
  }, [activeTab, loadAllMembers]);
  
  // Load the score from storage when the component mounts or when auth state changes
  useEffect(() => {
    // Set up auth state listener
    const unsubscribe = onAuthStateChanged(auth, (user: User | null) => {
      // Load score whenever auth state changes
      const loadScore = async () => {
        setLoading(true);
        try {
          if (user) {
            // Ensure account exists in Firestore for this user
            await FirebaseScoreService.ensureAccountExists();
            
            // Handle multi-member accounts
            const activeMemberId = await FirebaseScoreService.getActiveMember();
            if (activeMemberId) {
              setCurrentMemberId(activeMemberId);
              const membersData = await FirebaseScoreService.getMembers();
              setMembers(membersData as Members);
            }
          } else {
            // User signed out - clear state
            setCurrentMemberId(null);
            setMembers({});
          }
        } catch (error) {
          console.error('Error loading score:', error);
        } finally {
          setLoading(false);
        }
      };
      
      loadScore();
    });
    
    // Clean up subscription
    return () => unsubscribe();
  }, []);

  

  // Handle main tab change
  const handleTabChange = (newValue: number) => {
    setActiveTab(newValue);
    
    // Load members when switching to Scores tab
    if (newValue === 2) { // Scores tab
      loadAllMembers();
    }
  };



  // Handle member change - memoized to prevent infinite re-renders
  const handleMemberChange = useCallback(async (memberId: string) => {
    try {
      setCurrentMemberId(memberId);
      await loadAllMembers();
    } catch (error) {
      console.error('Error changing member:', error);
    }
  }, [loadAllMembers]);

  // Handle members update (when members are added/removed)
  const handleMembersUpdate = async () => {
    try {
      // Refresh current member info
      if (currentMemberId) {
        const membersData = await FirebaseScoreService.getMembers();
        if (membersData[currentMemberId]) {
          // setMemberName(membersData[currentMemberId].name);
        } else {
          // Current member was removed, get new active member
          const activeMemberId = await FirebaseScoreService.getActiveMember();
          if (activeMemberId) {
            await handleMemberChange(activeMemberId);
          }
        }
      }
    } catch (error) {
      console.error('Error updating members:', error);
    }
  };

  // Render the appropriate content based on the active tab
  const renderContent = () => {
    switch (activeTab) {
      case 0: // Scores
        return (
          <Scores
            scoreService={FirebaseScoreService}
            currentMemberId={currentMemberId}
            onMemberChange={handleMemberChange}
            members={members}
            loadAllMembers={loadAllMembers}
          />
        );
        
      case 1: // Leaderboard
        return (
          <Paper elevation={1}>
            <CardContent>
              <Leaderboard scoreService={FirebaseScoreService} members={members} />
            </CardContent>
          </Paper>
        );

      case 2: // Members
        return (
          <Paper elevation={1}>
              <MembersComponent 
                scoreService={FirebaseScoreService}
                onMembersUpdate={handleMembersUpdate}
              />
          </Paper>
        );

      case 3: // Authentication
        return (
          <Paper elevation={1}>
              <Authentication />
          </Paper>
        );
        
      default:
        return null;
    }
  };

  // Handle menu open/close
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuItemClick = (index: number) => {
    handleTabChange(index);
    handleMenuClose();
  };
  
  const menuItems = [
    { label: 'Scores', icon: <ScoreboardIcon /> },
    { label: 'Leaderboard', icon: <LeaderboardIcon /> },
    { label: 'Members', icon: <PeopleIcon /> },
    { label: 'Authentication', icon: <LoginIcon /> },
  ];

  return (
    <Box>
      {/* App Bar with Menu */}
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Score Tracker - {menuItems[activeTab]?.label}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleMenuOpen}
            sx={{ ml: 2 }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {menuItems.map((item, index) => (
          <MenuItem
            key={item.label}
            selected={activeTab === index}
            onClick={() => handleMenuItemClick(index)}
          >
            <ListItemIcon>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </MenuItem>
        ))}
      </Menu>

      {/* Main Content */}
      <Container maxWidth="sm" sx={{ mt: 3 }}>
        {renderContent()}
      </Container>
    </Box>
  );
}

export default App;
